import type { PlaygroundLanguage } from "../config/languages";
import { defaultLocale } from "@/lib/i18n";
import { SITE_URL } from "@/lib/site-url";

const PLAYGROUND_PUBLIC_URL = `${SITE_URL.replace(/\/$/, "")}/${defaultLocale}/playground`;

function lineCommentPrefix(languageId: string): string {
  switch (languageId) {
    case "python":
    case "ruby":
      return "#";
    default:
      return "//";
  }
}

function formatHeaderLines(prefix: string, lang: PlaygroundLanguage, isoDate: string): string {
  const line1 = `${prefix} Created with Progsity IDE: ${PLAYGROUND_PUBLIC_URL}`;
  const line2 = `${prefix} Language: ${lang.name} | Date: ${isoDate}`;
  return `${line1}\n${line2}\n`;
}

/** Insert PHP header after `<?php` opening line so the file stays valid. */
function prependPhpHeader(source: string, lang: PlaygroundLanguage, isoDate: string): string {
  const withoutBom = source.replace(/^\uFEFF/, "");
  const m = /^<\?php[^\n]*(?:\n|$)/.exec(withoutBom);
  const prefix = "//";
  const headerLines = formatHeaderLines(prefix, lang, isoDate);
  if (m && m.index === 0) {
    const end = m[0].length;
    return withoutBom.slice(0, end) + headerLines + withoutBom.slice(end);
  }
  return formatHeaderLines(prefix, lang, isoDate) + withoutBom;
}

/**
 * Prepends the Progsity IDE attribution header using language-appropriate line comments
 * (docs/24_PLAYGROUND_SPEC.md §10.7).
 */
export function prependProgsityIdeHeader(
  sourceCode: string,
  lang: PlaygroundLanguage,
  date: Date = new Date(),
): string {
  const iso = date.toISOString();
  if (lang.id === "php") {
    return prependPhpHeader(sourceCode, lang, iso);
  }
  const prefix = lineCommentPrefix(lang.id);
  return formatHeaderLines(prefix, lang, iso) + sourceCode.replace(/^\uFEFF/, "");
}

export function playgroundDownloadFilename(lang: PlaygroundLanguage): string {
  if (lang.id === "java") return "Main.java";
  return `main${lang.fileExtension}`;
}

export function triggerTextFileDownload(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
